// Segmenter for QUDT unit descriptions. Splits the raw string into text
// segments and LaTeX math segments at `$...$` boundaries. The math source
// (without the `$` delimiters) is passed downstream to KaTeX for rendering.
//
// QUDT prose mixes plain English with inline LaTeX, for example:
//   "A $\textit{Poundal Per Square Foot}$, ($pdl/ft^2$), is a unit ..."
//   "denoted by $^\circ$ (the degree symbol)"
//   "equivalent to $2\pi /360$ rad"
//
// We deliberately do NOT try to render math here — every new unit revealed
// more LaTeX commands we'd have to handle by hand (\circ, \pi, \frac, ...).
// KaTeX exists for exactly this reason; we just chop the input into the
// pieces it can chew.

export type DescriptionSegment =
  | { kind: "text"; tex?: never; value: string }
  | { kind: "math"; tex: string; value?: never };

/**
 * Walk `raw`, splitting on `$...$` math spans. Bare `\textit{X}` outside any
 * `$...$` is folded into a math segment with synthetic `\textit{X}` source so
 * the renderer handles it uniformly — never seen in real QUDT data, but
 * defends against upstream prose drift.
 *
 * Stray `$` (no closing pair) is emitted as plain text so an upstream issue
 * stays visible rather than getting silently swallowed.
 */
export function parseUnitDescription(
  raw: string | null | undefined,
): DescriptionSegment[] {
  if (!raw) return [];
  const segs: DescriptionSegment[] = [];
  let i = 0;
  let textBuf = "";
  const flushText = () => {
    if (textBuf) {
      segs.push({ kind: "text", value: textBuf });
      textBuf = "";
    }
  };
  while (i < raw.length) {
    const ch = raw[i];
    if (ch === "$") {
      const close = raw.indexOf("$", i + 1);
      if (close === -1) {
        // Stray opener — emit verbatim so the user can see the upstream issue.
        textBuf += ch;
        i++;
        continue;
      }
      flushText();
      segs.push({ kind: "math", tex: raw.slice(i + 1, close) });
      i = close + 1;
      continue;
    }
    if (ch === "\\" && raw.startsWith("\\textit{", i)) {
      const close = raw.indexOf("}", i + 8);
      if (close !== -1) {
        flushText();
        segs.push({
          kind: "math",
          tex: `\\textit{${raw.slice(i + 8, close)}}`,
        });
        i = close + 1;
        continue;
      }
    }
    textBuf += ch;
    i++;
  }
  flushText();
  return segs;
}
